import type {
  InterstateOnChangeEvent,
  SearchInputChangeEventValue,
  SearchInputProps,
  SearchInputSize,
  SearchInputStyle,
  SearchOption
} from "@dealer-center/core-components";
import { SearchInput } from "@dealer-center/core-components";
import React from "react";

import type {
  dealerSearchIncoming,
  dealerSearchOutgoing
} from "@dealer-center/ddc-pages-account-search-interface";
import styled from "styled-components";
import { useAccountSearchGet } from "@dealer-center/ddc-pages-account-search-fetch";

export const debounceTypingTime = 1000;

const StyledSearchInputArea = styled.div<{ submitButtonContent?: string }>`
  width: 100%;
  // this is a hack to replace the search icon with the word that is passed in with props
  ${props =>
    props.submitButtonContent &&
    `
    button {
    width: 5em;
    &:after {
      font-weight: bold;
      content: "${props.submitButtonContent}";
    }
    svg {
      display: none;
    }
  }
  `}
`;
/**
 * Props for the InputWithTypeahead component.
 */
export interface InputWithTypeaheadProps
  extends Omit<SearchInputProps, "onChange" | "style" | "size"> {
  size?: SearchInputSize;
  style?: SearchInputStyle;
  /**
   * Callback function to be called when the form is submitted.
   * @param account - The selected account.
   */
  onSubmit: (account: string) => void;

  /**
   * Optional callback function to be called when an item is selected from the typeahead.
   * @param account - The selected account.
   */
  onItemSelected?: (account: string) => void;

  /**
   * The initial value for the input field.
   */
  initialValue?: string;

  /**
   * The property to filter the typeahead results by.
   * Can be either "name" or "id".
   */
  filterBy?: "name" | "id";

  /**
   * The content of the submit button.
   */
  submitButtonContent?: string;
}

const shouldDisable = (
  filterBy: string,
  searchRequest: dealerSearchIncoming
) => {
  // if the filter is by id, we want to disable the search if the id is less than 3 characters
  if (filterBy === "id") {
    return (
      !searchRequest.ids ||
      searchRequest.ids.length === 0 ||
      searchRequest.ids[0].length < 3
    );
  }
  // if the filter is by name, we want to disable the search if the name is less than 3 characters
  return (
    !searchRequest.names ||
    searchRequest.names.length === 0 ||
    searchRequest.names[0].length < 3
  );
};

export const InputWithTypeahead = ({
  onSubmit,
  onItemSelected,
  initialValue,
  filterBy = "name",
  submitButtonContent,
  size = "medium",
  style = "primary",
  onInputChange,
  ...props
}: InputWithTypeaheadProps) => {
  const [searchRequest, updateSearchRequest] =
    React.useState<dealerSearchIncoming>({});
  const { data: matches } = useAccountSearchGet({
    page: 0,
    size: 100,
    disabled: shouldDisable(filterBy, searchRequest),
    searchRequest
  });
  const [options, setOptions] = React.useState<SearchOption[]>([]);

  React.useEffect(() => {
    const options: SearchOption[] = generateOptions(
      matches as dealerSearchOutgoing | null | undefined
    );
    setOptions(options);
  }, [matches, initialValue]);

  const searchForMatches = async (searchValue: string) => {
    if (!searchValue) {
      updateSearchRequest({});
      return;
    }
    if (filterBy === "name") {
      updateSearchRequest({
        names: [searchValue]
      });
    }
    if (filterBy === "id") {
      updateSearchRequest({
        ids: [`${searchValue}*`, searchValue]
      });
    }
  };

  const onSearchClick = (
    value: InterstateOnChangeEvent<SearchInputChangeEventValue> | undefined
  ) => {
    onSubmit(value?.target?.value || "");
  };

  const onInputChanged = (
    event: InterstateOnChangeEvent<SearchInputChangeEventValue>
  ) => {
    const displayValue = event?.target?.value;
    if (!displayValue || displayValue.length < 3) {
      searchForMatches("");
      return;
    }
    searchForMatches(displayValue);
    if (onInputChange) {
      onInputChange(event);
    }
  };

  const onMenuItemClicked = (value: InterstateOnChangeEvent<SearchOption>) => {
    if (onItemSelected) {
      onItemSelected(value.target?.value?.label);
    }
  };

  return (
    <StyledSearchInputArea submitButtonContent={submitButtonContent}>
      <SearchInput
        {...props}
        inputValue={initialValue}
        options={options}
        size={size}
        style={style}
        onChange={onMenuItemClicked}
        onInputChange={onInputChanged}
        onSearchClick={onSearchClick}
      />
    </StyledSearchInputArea>
  );
};
const generateOptions = (
  matches: dealerSearchOutgoing | null | undefined
): SearchOption[] =>
  matches?.hits?.slice(0, 100).map(match => ({
    label: match.id,
    value: match.id
  })) || [];
